import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

export type ThemePreference = 'light' | 'dark' | 'system';

/**
 * Syncs `data-bs-theme` on the document element for Bootstrap 5.3+ dark mode,
 * with `system` following `prefers-color-scheme` and persisting user choice.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = 'remmi-theme';

  readonly preference = signal<ThemePreference>('system');
  private readonly systemPrefersDark = signal(false);

  readonly resolved = computed<'light' | 'dark'>(() => {
    const p = this.preference();
    if (p === 'light') {
      return 'light';
    }
    if (p === 'dark') {
      return 'dark';
    }
    return this.systemPrefersDark() ? 'dark' : 'light';
  });

  constructor() {
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }
      this.doc.documentElement.setAttribute('data-bs-theme', this.resolved());
    });
  }

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const stored = localStorage.getItem(this.storageKey) as ThemePreference | null;
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      this.preference.set(stored);
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemPrefersDark.set(mq.matches);
    mq.addEventListener('change', (e) => this.systemPrefersDark.set(e.matches));
  }

  setPreference(value: ThemePreference): void {
    this.preference.set(value);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.storageKey, value);
    }
  }

  cyclePreference(): void {
    const order: ThemePreference[] = ['system', 'light', 'dark'];
    const idx = order.indexOf(this.preference());
    const safeIdx = idx >= 0 ? idx : 0;
    const next = order[(safeIdx + 1) % order.length]!;
    this.setPreference(next);
  }
}
